import React, { FunctionComponent } from 'react';
import { isNumber, isUndefined } from 'lodash';
import formatNumber from 'common/js_utils/formatNumber';
import getScopedTranslateMethod from 'common/js_utils/getScopedTranslateMethod';

const translate = getScopedTranslateMethod('common');

interface Props {
  /** (Optional) Displayed as the number of rows a dataset has */
  rowCount?: number;
  /** Displayed as the column count for a dataset */
  columnCount: number;
  /** What to label this metadata info */
  rowLabel: string;
  /** The row identifier */
  rowIdentifier?: string;
}

/** Renders a short <section> that lists the number of rows, number of columns,
 *  and row label of a dataset.
 *
 *  @note Row count may be fetched asynchronously, since we don't have this value
 *  immediately available (i.e. column stats service is not in use) and it may be
 *  expensive to get it on the fly for multi-million-row datasets (see EN-14299).
 */
const RowDetails: FunctionComponent<Props> = ({ rowCount, columnCount, rowLabel, rowIdentifier }) => {
  // Renders row count info. Omitted if row count is not a number.
  const renderRowCount = () => {
    // Legacy guard because there many files using this which are JS
    if (!isNumber(rowCount)) {
      return null;
    }

    return (
      <div className="metadata-pair">
        <dt className="metadata-pair-key">{translate('dataset_contents.rows')}</dt>

        <dd className="metadata-pair-value">{formatNumber(rowCount)}</dd>
      </div>
    );
  };

  // Renders column count info. Omitted if column count is not a positive number.
  const renderColumnCount = (): JSX.Element | null => {
    // Legacy guard because there many files using this which are JS
    if (!isNumber(columnCount) || columnCount <= 0) {
      return null;
    }

    return (
      <div className="metadata-pair">
        <dt className="metadata-pair-key">{translate('dataset_contents.columns')}</dt>
        <dd className="metadata-pair-value">{formatNumber(columnCount)}</dd>
      </div>
    );
  };

  // Renders row label info. Omitted if the row label is empty or default.
  const renderRowLabel = (): JSX.Element | null => {
    // Legacy guard because there many files using this which are JS
    if (!rowLabel || rowLabel.toLowerCase() === translate('default_row_label')) {
      return null;
    }

    return (
      <div className="metadata-pair">
        <dt className="metadata-pair-key">{translate('dataset_contents.row_display_unit')}</dt>
        <dd itemProp="variableMeasured" className="metadata-pair-value">
          {rowLabel}
        </dd>
      </div>
    );
  };

  // Renders row identifier info. Omitted if the row identifier is not defined or it is not a default view.
  const renderRowIdentifier = (): JSX.Element | null => {
    // Legacy guard because there many files using this which are JS
    if (!rowIdentifier) {
      return null;
    }

    return (
      <div className="metadata-pair">
        <dt className="metadata-pair-key">{translate('dataset_contents.row_identifier')}</dt>
        <dd className="metadata-pair-value">{rowIdentifier}</dd>
      </div>
    );
  };

  // Conditionally render row/column stats within the interior content box.
  // Shows a spinner while fetching the row count asychronously.
  const renderRowStats = () => {
    if (isUndefined(rowCount)) {
      return (
        <div className="metadata-section busy">
          <span className="spinner-default" />
        </div>
      );
    }

    return (
      <div className="metadata-section">
        <dl className="metadata-row">
          {renderRowCount()}
          {renderColumnCount()}
          {renderRowLabel()}
          {renderRowIdentifier()}
        </dl>
      </div>
    );
  };

  return (
    <section className="landing-page-section dataset-contents">
      <h2 className="forge-typography--heading4 landing-page-section-header">
        {translate('dataset_contents.title')}
      </h2>

      <div className="section-content">{renderRowStats()}</div>
    </section>
  );
};

export default RowDetails;
